import api from "./api"
import { requestResolution } from "../lib/conflictBus"

const getActiveReportId = () =>
  window.sessionStorage?.getItem("activeReportId") ||
  window.localStorage?.getItem("activeReportId") ||
  ""

export const siteDataService = {
  // Get all site data with filters
  getSiteData: async (filters = {}) => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        params.append(key, value)
      }
    })
    const response = await api.get(`/site-data?${params.toString()}`)
    return response.data
  },

  // Get site data by ID
  getSiteDataById: async (id, reportId = getActiveReportId()) => {
    const response = await api.get(`/site-data/${id}`, {
      params: reportId ? { reportId } : {},
    })
    return response.data
  },

  // Get site data by project
  getSiteDataByProject: async (projectId, filters = {}) => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        params.append(key, value)
      }
    })

    const response = await api.get(`/site-data/project/${projectId}?${params.toString()}`)
    return response.data
  },

  // Create new site data
  createSiteData: async (siteData) => {
    const response = await api.post("/site-data", siteData)
    return response.data
  },

  // Update site data
  updateSiteData: async (id, siteData, options = {}) => {
    const reportId = siteData?.reportId || getActiveReportId()
    const payload = {
      ...siteData,
      ...(reportId ? { reportId } : {}),
    }

    try {
      const response = await api.put(`/site-data/${id}`, payload)
      return response.data
    } catch (error) {
      // Phase 5 — the server detected the record changed since this client
      // last read it (e.g. two auditors synced offline edits to the same
      // site). Let the auditor choose which version wins instead of
      // silently overwriting or silently discarding.
      const serverRecord = error?.response?.data?.serverRecord
      if (error?.response?.status !== 409 || !serverRecord) throw error

      const choice = await requestResolution({
        entityLabel: options.entityLabel || "This site data record",
        serverRecord,
        localData: payload,
      })

      if (choice === "overwrite") {
        const forcedResponse = await api.put(`/site-data/${id}`, {
          ...payload,
          baseVersion: serverRecord.updatedAt,
          forceOverwrite: true,
        })
        return forcedResponse.data
      }

      // "discard" — the auditor chose to keep the server's version. Hand it
      // back so the caller can refresh its form/state instead of treating
      // this like a failed save.
      return { success: true, data: serverRecord, discarded: true }
    }
  },

  // Delete site data
  deleteSiteData: async (id) => {
    const response = await api.delete(`/site-data/${id}`)
    return response.data
  },

  // Bulk create site data
  bulkCreateSiteData: async (siteDataArray) => {
    const response = await api.post("/site-data/bulk", { siteData: siteDataArray })
    return response.data
  },

  // Export site data
  exportSiteData: async (projectId, format = "csv") => {
    const response = await api.get(`/site-data/export/${projectId}?format=${format}`, {
      responseType: "blob",
    })
    return response.data
  },
}
